/* eslint-disable no-param-reassign */
import {IElement} from '../../../incase-domain/elements';

type ResizeEdges = NonNullable<Interact.ResizeEvent['edges']>;

function start(event: Interact.ResizeEvent, element: IElement, elementPosition: {x: number; y: number}): void {
    const targetEl = event.target as HTMLElement;
    const {height, width} = element;

    targetEl.style.userSelect = 'none';

    const data = {
        ...elementPosition,
        height: height || '0%',
        width: width || '0%',
    };

    setDatasetAttrs(targetEl, data);
    setStyleAttrs(targetEl, data);
}

/** Mirrors interact.modifiers.aspectRatio edge linking — only top/left matter for anchoring. */
function linkedTopLeft(edges: ResizeEdges): {top: boolean; left: boolean} {
    return {
        top: !!(edges.top || (edges.left && !edges.bottom)),
        left: !!(edges.left || (edges.top && !edges.right)),
    };
}

function move(event: Interact.ResizeEvent, clientWidth: number, clientHeight: number, scale = 1, aspectRatio?: number): void {
    const targetEl = event.target as HTMLElement;
    const prevX = Number(targetEl.dataset.x);
    const prevY = Number(targetEl.dataset.y);
    const prevW = (parseFloat(targetEl.dataset.width ?? '0') / 100) * clientWidth;
    const prevH = (parseFloat(targetEl.dataset.height ?? '0') / 100) * clientHeight;

    let x = prevX;
    let y = prevY;
    let w = event.rect.width / scale;
    let h = event.rect.height / scale;

    if (aspectRatio && event.edges) {
        const {top, left} = linkedTopLeft(event.edges);
        const right = prevX + prevW;
        const bottom = prevY + prevH;

        if (event.edges.left || event.edges.right) h = w / aspectRatio;
        else w = h * aspectRatio;

        const maxW = left ? right : clientWidth - prevX;
        const maxH = top ? bottom : clientHeight - prevY;
        w = Math.min(w, maxW, maxH * aspectRatio);
        h = w / aspectRatio;

        x = left ? right - w : prevX;
        y = top ? bottom - h : prevY;
    } else {
        if (event.edges?.top && event.deltaRect) y -= event.deltaRect.height / scale;
        if (event.edges?.left && event.deltaRect) x -= event.deltaRect.width / scale;

        if (x < 0) {
            w += x;
            x = 0;
        }
        if (y < 0) {
            h += y;
            y = 0;
        }
        w = Math.min(w, clientWidth - x);
        h = Math.min(h, clientHeight - y);
    }

    const data = {
        x,
        y,
        width: `${(Math.max(0, w) / clientWidth) * 100}%`,
        height: `${(Math.max(0, h) / clientHeight) * 100}%`,
    };

    setDatasetAttrs(targetEl, data);
    setStyleAttrs(targetEl, data);
}

function end(
    event: Interact.ResizeEvent,
    elementPosition: {x: number; y: number},
    clientWidth: number,
    clientHeight: number,
    updateElement: (element: Partial<IElement>) => void
): void {
    const targetEl = event.target as HTMLElement;

    updateElement({
        xPosition: (elementPosition.x / clientWidth) * 100,
        yPosition: (elementPosition.y / clientHeight) * 100,
        width: targetEl.dataset.width ?? '0%',
        height: targetEl.dataset.height ?? '0%',
    });
}

type Box = {x: number; y: number; height: string; width: string};

function setStyleAttrs(targetEl: HTMLElement, d: Box): void {
    targetEl.style.left = `${d.x}px`;
    targetEl.style.top = `${d.y}px`;
    targetEl.style.height = d.height;
    targetEl.style.width = d.width;
}

function setDatasetAttrs(targetEl: HTMLElement, d: Box): void {
    targetEl.dataset.x = String(d.x);
    targetEl.dataset.y = String(d.y);
    targetEl.dataset.height = d.height;
    targetEl.dataset.width = d.width;
}

export const resize = {start, move, end};
